// The UI and API are served from the same origin: nginx (prod) or the Vite dev
// server (dev) proxies /api to the FastAPI service. So every call is a plain
// same-origin fetch to /api/v1/... with a Bearer token from localStorage.

const API_BASE = "/api/v1";

const TOKEN_KEY = "rcpt_access_token";
const REFRESH_KEY = "rcpt_refresh_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setTokens(access: string, refresh: string) {
  localStorage.setItem(TOKEN_KEY, access);
  localStorage.setItem(REFRESH_KEY, refresh);
}

export function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

function authHeaders(json = true): Record<string, string> {
  const headers: Record<string, string> = json ? { "Content-Type": "application/json" } : {};
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

// Silent refresh: on a 401, swap the refresh token for a new pair and replay
// the request once. Concurrent 401s share one in-flight refresh.
let refreshInFlight: Promise<boolean> | null = null;

async function performRefresh(): Promise<boolean> {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) return false;
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) {
      clearTokens();
      return false;
    }
    const data = await res.json();
    setTokens(data.access_token, data.refresh_token);
    return true;
  } catch {
    return false; // offline: keep tokens, surface the original 401
  }
}

function tryRefresh(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** fetch() with auth + one silent-refresh retry. For blob endpoints. */
export async function authedFetch(url: string, init: RequestInit = {}, retried = false): Promise<Response> {
  const res = await fetch(url, {
    ...init,
    headers: { ...authHeaders(false), ...(init.headers as Record<string, string> | undefined) },
  });
  if (res.status === 401 && !retried && (await tryRefresh())) return authedFetch(url, init, true);
  return res;
}

async function errorMessage(res: Response): Promise<string> {
  const body = await res.json().catch(() => ({}));
  if (typeof body.detail === "string") return body.detail;
  if (Array.isArray(body.detail)) return body.detail.map((d: { msg?: string }) => d.msg).join("; ");
  return `Request failed: ${res.status}`;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  opts: { authRequired?: boolean; retried?: boolean } = {},
): Promise<T> {
  const isForm = options.body instanceof FormData;
  const headers = {
    ...(opts.authRequired === false ? (isForm ? {} : { "Content-Type": "application/json" }) : authHeaders(!isForm)),
    ...(options.headers as Record<string, string> | undefined),
  };
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  if (res.status === 401 && opts.authRequired !== false) {
    if (!opts.retried && (await tryRefresh())) return request<T>(path, options, { ...opts, retried: true });
    clearTokens();
    if (window.location.pathname.startsWith("/app") || window.location.pathname.startsWith("/admin")) {
      window.location.href = "/login";
    }
    throw new Error("Unauthorized");
  }
  if (!res.ok) throw new Error(await errorMessage(res));
  if (res.status === 204) return undefined as T;
  return res.json();
}

function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : "";
}

// ─── Auth ──────────────────────────────────────────────────────────

export interface Tokens {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

export interface User {
  id: string;
  email: string;
  full_name: string;
  is_admin: boolean;
  is_active: boolean;
  created_at: string;
  last_login_at?: string | null;
  /** When the new-user tour was finished or skipped; null = show it. */
  tutorial_done_at?: string | null;
}

export const tutorial = {
  /** Create (or reuse) the tour's sample receipts; returns the one the tour opens. */
  start: () => request<{ receipt_id: string; vendor_name: string }>("/tutorial/start", { method: "POST" }),
  /** Delete the samples and mark the tour done. */
  finish: () => request<{ removed: number }>("/tutorial/finish", { method: "POST" }),
};

export const auth = {
  config: () => request<{ allow_registration: boolean; first_user: boolean }>("/auth/config", {}, { authRequired: false }),
  login: (email: string, password: string) =>
    request<Tokens>("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }, { authRequired: false }),
  register: (email: string, password: string, full_name: string) =>
    request<Tokens>(
      "/auth/register",
      { method: "POST", body: JSON.stringify({ email, password, full_name }) },
      { authRequired: false },
    ),
  me: () => request<User>("/auth/me"),
  /** After a password change, the response carries this session's new tokens. */
  updateMe: (body: { full_name?: string; current_password?: string; new_password?: string }) =>
    request<User & { access_token?: string | null; refresh_token?: string | null }>("/auth/me", {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
};

// ─── Mailboxes ─────────────────────────────────────────────────────

export type MailProvider = "gmail" | "outlook" | "imap";

export interface EmailAccount {
  id: string;
  owner_user_id: string;
  provider: MailProvider;
  email_address: string;
  display_name?: string | null;
  is_active: boolean;
  poll_interval_seconds: number;
  backfill_history: boolean;
  import_since?: string | null;
  last_polled_at?: string | null;
  last_poll_started_at?: string | null;
  poll_requested_at?: string | null;
  last_error?: string | null;
  consecutive_failures: number;
  created_at: string;
  updated_at: string;
}

export interface HistoryOptions {
  backfill_history?: boolean;
  import_since?: string | null;
}

export interface ImapConnectRequest extends HistoryOptions {
  email_address: string;
  display_name?: string | null;
  host: string;
  port: number;
  username: string;
  password: string;
  use_ssl: boolean;
}

export interface AccountUpdate {
  display_name?: string | null;
  is_active?: boolean;
  poll_interval_seconds?: number;
}

export type MessageStatus = "pending" | "processing" | "parsed" | "skipped" | "error";

export interface EmailMessage {
  id: string;
  account_id: string;
  owner_user_id: string;
  received_at?: string | null;
  sender_email?: string | null;
  sender_name?: string | null;
  subject?: string | null;
  status: MessageStatus;
  status_reason?: string | null;
  attempts: number;
  receipt_count: number;
  processed_at?: string | null;
  created_at: string;
}

export const accounts = {
  list: () => request<EmailAccount[]>("/accounts/"),
  providers: () => request<Record<MailProvider, boolean>>("/accounts/providers"),
  startOAuth: (provider: "gmail" | "outlook", body: HistoryOptions & { display_name?: string | null }) =>
    request<{ authorization_url: string }>("/accounts/oauth/start", {
      method: "POST",
      body: JSON.stringify({ provider, ...body }),
    }),
  connectImap: (body: ImapConnectRequest) =>
    request<EmailAccount>("/accounts/imap", { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, body: AccountUpdate) =>
    request<EmailAccount>(`/accounts/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  remove: (id: string, deleteReceipts = false) =>
    request<void>(`/accounts/${id}${qs({ delete_receipts: deleteReceipts })}`, { method: "DELETE" }),
  triggerPoll: (id: string) => request<{ status: string }>(`/accounts/${id}/poll`, { method: "POST" }),
  test: (id: string) => request<{ ok: boolean; detail?: string | null }>(`/accounts/${id}/test`, { method: "POST" }),
};

export const messages = {
  list: (filters: { status?: MessageStatus; account_id?: string; limit?: number; offset?: number } = {}) =>
    request<EmailMessage[]>(`/messages/${qs(filters)}`),
  forceParse: (id: string) => request<{ status: string }>(`/messages/${id}/parse`, { method: "POST" }),
};

// ─── Receipts ──────────────────────────────────────────────────────

export type ReceiptStatus = "pending" | "reviewed" | "error";

/** `receipt` = paid (QBO Purchase), `bill` = owed (QBO Bill), `income` = received (QBO SalesReceipt). */
export type ReceiptType = "receipt" | "bill" | "income";

export interface ReceiptAttachment {
  filename: string;
  content_type: string;
  size_bytes: number;
  file_id: string;
}

export interface ReceiptAttachmentMeta {
  filename?: string | null;
  content_type?: string | null;
  size_bytes?: number | null;
  file_id?: string | null;
}

export interface RecipientRef {
  email: string;
  name?: string | null;
}

export interface ReceiptListItem {
  id: string;
  user_id: string;
  source: "email" | "upload";
  email_account_id?: string | null;
  email_message_id?: string | null;
  upload_job_id?: string | null;
  status: ReceiptStatus;
  receipt_type: ReceiptType;
  vendor_name?: string | null;
  vendor_email?: string | null;
  vendor_address?: string | null;
  amount_cents?: number | null;
  total_cents?: number | null;
  tax_cents?: number | null;
  /** Each tax as printed on the document. */
  tax_breakdown?: { type: string; rate: number | null; amount_cents: number | null }[] | null;
  /** Set & Ledger tax code key (see receipts.taxCodes); manual = picked by the user. */
  tax_code?: string | null;
  tax_code_manual?: boolean;
  currency?: string | null;
  invoice_number?: string | null;
  invoice_date?: string | null;
  due_date?: string | null;
  card_brand?: string | null;
  card_last4?: string | null;
  description?: string | null;
  ai_confidence?: number | null;
  subject?: string | null;
  sender?: string | null;
  received_at: string;
  recipients_to?: RecipientRef[] | null;
  recipients_cc?: RecipientRef[] | null;
  attachment_file_ids?: ReceiptAttachment[] | null;
  attachments_meta?: ReceiptAttachmentMeta[] | null;
  qbo_sync_status?: string;
  qbo_synced_at?: string | null;
  qbo_sync_error?: string | null;
  discarded?: boolean;
  discard_reason?: string | null;
  /** Made for the new-user tour; deleted when it ends. */
  is_sample?: boolean;
  /** Set when this receipt is hidden as a copy of another one. */
  duplicate_of_id?: string | null;
  duplicate_match?: DuplicateMatch | null;
  keep_separate?: boolean;
  created_at: string;
  updated_at: string;
}

/** `invoice` = same vendor + invoice number; `transaction` = same card, date, amount and sender domain. */
/** `payment`: a paid receipt settling a bill (same vendor, amount and currency). */
export type DuplicateMatch = "invoice" | "transaction" | "payment";

export interface Receipt extends ReceiptListItem {
  body_text_full?: string | null;
  body_html_full?: string | null;
  ai_extraction?: Record<string, unknown> | null;
}

export interface ReceiptUpdate {
  status?: ReceiptStatus;
  receipt_type?: ReceiptType;
  vendor_name?: string | null;
  total_cents?: number | null;
  tax_cents?: number | null;
  currency?: string | null;
  invoice_number?: string | null;
  invoice_date?: string | null;
  due_date?: string | null;
  card_brand?: string | null;
  card_last4?: string | null;
  description?: string | null;
  /** A tax code key; null = detect it from the tax again. */
  tax_code?: string | null;
}

/** `vendor` = who you pay (receipt + bill), `client` = who pays you (income). */
export type EntityKind = "vendor" | "client";

export interface ReceiptListFilters {
  status?: ReceiptStatus;
  type?: ReceiptType;
  kind?: EntityKind;
  since?: string;
  vendor_name?: string;
  card_last4?: string;
  card_brand?: string;
  has_attachments?: boolean;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface QueueItem {
  id: string;
  status: ReceiptStatus;
}

export interface PaginatedReceipts {
  items: ReceiptListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface CurrencyTotal {
  currency: string | null;
  total_cents: number;
}

export interface CardSummary {
  card_brand: string | null;
  card_last4: string;
  receipt_count: number;
  /** One entry per currency, largest first (amounts are never added across currencies). */
  totals: CurrencyTotal[];
  /** The largest entry of `totals`, for sorting. */
  total_spent_cents: number | null;
  currency: string | null;
}

export interface VendorSummary {
  vendor_name: string;
  receipt_count: number;
  totals: CurrencyTotal[];
  total_spent_cents: number | null;
  currency: string | null;
}

export interface ReceiptStats {
  total: number;
  by_type: Partial<Record<ReceiptType, number>>;
  by_qbo_status: Record<string, number>;
  spend_30d: { currency: string; total_cents: number }[];
}

export type UploadJobStatus = "pending" | "processing" | "done" | "error";

export interface UploadReceiptSummary {
  id: string;
  vendor_name: string | null;
  total_cents: number | null;
  currency: string | null;
  receipt_type: string;
  discarded: boolean;
}

export interface UploadJob {
  id: string;
  status: UploadJobStatus;
  total_files: number;
  processed_files: number;
  filenames: string[] | null;
  created_receipt_ids: string[] | null;
  message: string | null;
  error: string | null;
  created_at: string;
  updated_at: string;
  receipts: UploadReceiptSummary[] | null;
}

export type ParseReportReason = "wrong_vendor" | "wrong_amount" | "wrong_date" | "wrong_type" | "other";
export type ParseReportStatus = "open" | "resolved" | "dismissed";

export interface ParseReport {
  id: string;
  receipt_id: string | null;
  owner_user_id: string;
  reason: ParseReportReason;
  details: string | null;
  status: ParseReportStatus;
  resolution_note: string | null;
  reported_sender: string | null;
  reported_subject: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
}

export const receipts = {
  list: (filters: ReceiptListFilters = {}) => request<ReceiptListItem[]>(`/receipts/${qs({ ...filters })}`),
  listPaginated: (filters: ReceiptListFilters = {}) =>
    request<PaginatedReceipts>(`/receipts/page/${qs({ ...filters })}`),
  /** Id + status of every receipt matching the filters, in list order. */
  queue: (filters: ReceiptListFilters = {}) => request<QueueItem[]>(`/receipts/queue/${qs({ ...filters })}`),
  bulkStatus: (ids: string[], status: ReceiptStatus) =>
    request<{ updated: number }>("/receipts/bulk-status", { method: "POST", body: JSON.stringify({ ids, status }) }),
  stats: () => request<ReceiptStats>("/receipts/stats/"),
  listCards: () => request<CardSummary[]>("/receipts/cards/"),
  mergeCards: (
    source: { card_last4: string; card_brand?: string | null },
    dest: { card_last4: string; card_brand?: string | null },
  ) =>
    request<{ moved: number }>("/receipts/cards/merge", {
      method: "PATCH",
      body: JSON.stringify({
        source_card_last4: source.card_last4,
        source_card_brand: source.card_brand ?? null,
        dest_card_last4: dest.card_last4,
        dest_card_brand: dest.card_brand ?? null,
      }),
    }),
  listVendors: (kind?: EntityKind) => request<VendorSummary[]>(`/receipts/vendors/${qs({ kind })}`),
  get: (id: string) => request<Receipt>(`/receipts/${id}`),
  taxCodes: () => request<AppTaxCode[]>("/receipts/tax-codes/"),
  update: (id: string, body: ReceiptUpdate) =>
    request<Receipt>(`/receipts/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  remove: (id: string) => request<void>(`/receipts/${id}`, { method: "DELETE" }),
  reparse: (id: string) => request<{ status: string; message: string }>(`/receipts/${id}/reparse`, { method: "POST" }),
  /** Hidden copies that point at this receipt. */
  duplicates: (id: string) => request<ReceiptListItem[]>(`/receipts/${id}/duplicates`),
  /** "Not a duplicate": show the copy on its own and reparse it. */
  split: (id: string) => request<{ message: string; queued: boolean }>(`/receipts/${id}/split`, { method: "POST" }),
  report: (receiptId: string, body: { reason: ParseReportReason; details?: string | null }) =>
    request<ParseReport>("/parse-reports/", { method: "POST", body: JSON.stringify({ receipt_id: receiptId, ...body }) }),
  listReports: (receiptId: string) => request<ParseReport[]>(`/parse-reports/for-receipt/${receiptId}`),
  removeByAccount: (emailAccountId: string) =>
    request<{ deleted: number }>(`/receipts/by-account/${emailAccountId}`, { method: "DELETE" }),
  setCurrencyForVendor: (vendorName: string, currency: string) =>
    request<{ updated: number }>("/receipts/by-vendor", {
      method: "PATCH",
      body: JSON.stringify({ vendor_name: vendorName, currency }),
    }),
  renameVendor: (vendorName: string, newName: string) =>
    request<{ updated: number }>("/receipts/vendors/rename", {
      method: "PATCH",
      body: JSON.stringify({ vendor_name: vendorName, new_vendor_name: newName }),
    }),
  mergeVendors: (sourceName: string, destName: string) =>
    request<{ moved: number }>("/receipts/vendors/merge", {
      method: "PATCH",
      body: JSON.stringify({ source_vendor_name: sourceName, dest_vendor_name: destName }),
    }),
  attachmentUrl: (receiptId: string, fileId: string) => `${API_BASE}/receipts/${receiptId}/attachments/${fileId}`,
  uploadFiles: (files: File[]) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    return request<UploadJob>("/uploads", { method: "POST", body: fd });
  },
  getUploadJob: (jobId: string) => request<UploadJob>(`/uploads/${jobId}`),
  listUploadJobs: () => request<UploadJob[]>("/uploads"),
};

// ─── AI review ─────────────────────────────────────────────────────

export type ReviewStatus = "none" | "pending" | "processing" | "done" | "error";
export type Severity = "high" | "medium" | "low";

/** A receipt field and the value the AI says it should have. */
export interface ReviewFix {
  field: keyof ReceiptUpdate;
  value: string | number;
}

export interface ReviewFinding {
  receipt_id: string;
  severity: Severity;
  issue: string;
  fix: ReviewFix | null;
  /** The suggested fix is in place; the receipt just needs marking reviewed. */
  fixed: boolean;
}

export interface ReviewGroup {
  kind: EntityKind;
  /** "" when the receipts have no vendor name. */
  vendor_name: string;
  pending_count: number;
  total_count: number;
  status: ReviewStatus;
  /** Receipts changed since the AI looked. */
  stale: boolean;
  flagged_count: number;
  high_count: number;
  checked_at: string | null;
  /** Only tour samples. */
  sample: boolean;
}

export interface ReviewDetail extends ReviewGroup {
  summary: string | null;
  error: string | null;
  findings: ReviewFinding[];
  /** The receipts still needing review, newest first. */
  receipts: ReceiptListItem[];
}

/** Fields a fix can be saved for a whole vendor (applies to future receipts). */
export const RULE_FIELDS: ReadonlyArray<keyof ReceiptUpdate> = ["receipt_type", "currency", "card_brand", "card_last4"];

export const review = {
  list: () => request<ReviewGroup[]>("/review/"),
  /** One vendor/client (always re-checks), or every one whose check is missing or stale. */
  check: (target?: { kind: EntityKind; vendor_name: string }) =>
    request<{ queued: number }>("/review/check", { method: "POST", body: JSON.stringify(target ?? {}) }),
  group: (kind: EntityKind, name: string) => request<ReviewDetail>(`/review/group${qs({ kind, name })}`),
  /** Apply a fix to these receipts and every future one from the vendor. */
  always: (body: { kind: EntityKind; vendor_name: string; field: string; value: string; receipt_ids: string[] }) =>
    request<{ updated: number }>("/review/rule", { method: "POST", body: JSON.stringify(body) }),
};

// ─── Reports ───────────────────────────────────────────────────────

export type ReportGroupBy = "counterparty" | "card" | "type" | "month" | "quarter" | "year";
export type ReportPeriod =
  | "this_month" | "last_month" | "this_quarter" | "last_quarter" | "this_year" | "last_year"
  | "last_12_months" | "all" | "custom";

export interface ReportSpec {
  group_by: ReportGroupBy;
  types: ReceiptType[];
  /** Relative periods keep a saved report current; "custom" uses the dates. */
  period: ReportPeriod;
  date_from: string | null;
  date_to: string | null;
  status: "pending" | "reviewed" | null;
}

export interface ReportTotal {
  currency: string | null;
  total_cents: number;
  tax_cents: number;
}

export interface ReportRow {
  /** "" = none (no vendor name, no card). */
  key: string;
  count: number;
  /** One per currency, largest first: never added across currencies. */
  totals: ReportTotal[];
}

export interface ReportResult {
  group_by: ReportGroupBy;
  rows: ReportRow[];
  count: number;
  totals: ReportTotal[];
  date_from: string | null;
  date_to: string | null;
}

export interface SavedReport {
  id: string;
  name: string;
  spec: ReportSpec;
  created_at: string;
  updated_at: string;
}

export const reports = {
  run: (spec: ReportSpec) => request<ReportResult>("/reports/run", { method: "POST", body: JSON.stringify(spec) }),
  list: () => request<SavedReport[]>("/reports/"),
  save: (name: string, spec: ReportSpec) =>
    request<SavedReport>("/reports/", { method: "POST", body: JSON.stringify({ name, spec }) }),
  update: (id: string, body: { name?: string; spec?: ReportSpec }) =>
    request<SavedReport>(`/reports/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  remove: (id: string) => request<void>(`/reports/${id}`, { method: "DELETE" }),
};

// ─── Grouping rules ────────────────────────────────────────────────

export type GroupRuleType = "vendor_rename" | "vendor_combine" | "vendor_match" | "card_combine" | "vendor_field";

export interface GroupRule {
  id: string;
  rule_type: GroupRuleType;
  source_vendor_name: string | null;
  target_vendor_name: string | null;
  source_card_brand: string | null;
  source_card_last4: string | null;
  target_card_brand: string | null;
  target_card_last4: string | null;
  /** vendor_field: always set this field to this value for source_vendor_name. */
  field_name: string | null;
  field_value: string | null;
  created_at: string;
}

export const groupRules = {
  list: () => request<GroupRule[]>("/receipts/group-rules/"),
  remove: (id: string) => request<void>(`/receipts/group-rules/${id}`, { method: "DELETE" }),
};

// ─── QuickBooks Online ─────────────────────────────────────────────

export interface QboStatus {
  connected: boolean;
  mode: "fake" | "real" | string;
  id?: string | null;
  company_name?: string | null;
  realm_id_last4?: string | null;
  sync_enabled?: boolean;
  auto_sync?: boolean;
  default_expense_account_id?: string | null;
  default_expense_account_name?: string | null;
  default_payment_account_id?: string | null;
  default_payment_account_name?: string | null;
  default_payment_type?: string | null;
  default_ap_account_id?: string | null;
  default_ap_account_name?: string | null;
  default_income_item_id?: string | null;
  default_income_item_name?: string | null;
  default_deposit_account_id?: string | null;
  default_deposit_account_name?: string | null;
  /** Sales-tax companies: the code for lines with no tax charged. */
  no_tax_code_id?: string | null;
  no_tax_code_name?: string | null;
  last_sync_at?: string | null;
  pending_count?: number;
  created_at?: string | null;
}

export interface QboAccount {
  id: string;
  name: string;
  account_type?: string | null;
  account_sub_type?: string | null;
  classification?: string | null;
}

export interface QboItem {
  id: string;
  name: string;
  type?: string | null;
}

/** One of Set & Ledger's tax codes. */
export interface AppTaxCode {
  key: string;
  label: string;
  rate: number;
  components: { kind: string; rate: number }[];
}

/** Set & Ledger tax codes and the QuickBooks code each posts with. */
export interface QboTaxOverview {
  uses_tax_codes: boolean;
  country: string | null;
  codes: {
    key: string;
    label: string;
    rate: number;
    receipt_count: number;
    pending_count: number;
    qbo_tax_code_id: string | null;
    qbo_tax_code_name: string | null;
    /** mapped = chosen by the user; auto = matched by rate; none = no QuickBooks code. */
    source: "mapped" | "auto" | "none";
    creatable: boolean;
  }[];
  qbo_codes: QboTaxCode[];
  /** Waiting receipts whose tax posts with a note (into the cost, or under another code). */
  receipts: {
    receipt_id: string;
    vendor_name: string | null;
    invoice_date: string | null;
    total_cents: number;
    tax_cents: number | null;
    currency: string | null;
    tax_code: string;
    note: string;
    lines: { amount_cents: number; tax_code: string | null }[];
  }[];
}

/** A sales-tax code; rates are total percentages (null = not used on that side). */
export interface QboTaxCode {
  id: string;
  name: string;
  purchase_rate: number | null;
  sales_rate: number | null;
}

export interface QboSyncResult {
  receipt_id: string;
  status: "ok" | "skipped" | "error";
  qbo_entity_type?: string | null;
  qbo_entity_id?: string | null;
  message?: string | null;
}

export interface QboBulkSyncResult {
  synced: number;
  skipped: number;
  errors: number;
  results: QboSyncResult[];
}

export interface QboActivityRow {
  id: string;
  kind: string;
  direction: string;
  entity_type?: string | null;
  entity_id?: string | null;
  qbo_entity_id?: string | null;
  status: string;
  message?: string | null;
  created_at: string;
}

export type QboConnectionUpdate = Partial<
  Pick<
    QboStatus,
    | "sync_enabled"
    | "auto_sync"
    | "default_expense_account_id"
    | "default_expense_account_name"
    | "default_payment_account_id"
    | "default_payment_account_name"
    | "default_payment_type"
    | "default_ap_account_id"
    | "default_ap_account_name"
    | "default_income_item_id"
    | "default_income_item_name"
    | "default_deposit_account_id"
    | "default_deposit_account_name"
  >
>;

export const quickbooks = {
  status: () => request<QboStatus>("/qbo/status"),
  connectStart: (returnUrl: string) =>
    request<{ authorize_url: string; state: string }>("/qbo/connect", {
      method: "POST",
      body: JSON.stringify({ return_url: returnUrl }),
    }),
  update: (body: QboConnectionUpdate) =>
    request<QboStatus>("/qbo/status", { method: "PATCH", body: JSON.stringify(body) }),
  disconnect: () => request<void>("/qbo", { method: "DELETE" }),
  accounts: () => request<QboAccount[]>("/qbo/accounts"),
  items: () => request<QboItem[]>("/qbo/items"),
  taxCodes: () => request<QboTaxCode[]>("/qbo/tax-codes"),
  taxMappings: () => request<QboTaxOverview>("/qbo/tax-mappings"),
  /** Map a Set & Ledger tax code to a QuickBooks code (null = automatic). */
  setTaxMapping: (code: string, qboTaxCodeId: string | null) =>
    request<QboTaxOverview>(`/qbo/tax-mappings/${code}`, {
      method: "PUT",
      body: JSON.stringify({ qbo_tax_code_id: qboTaxCodeId }),
    }),
  /** Create the QuickBooks code for a Set & Ledger code (GST/HST) and map it. */
  createTaxMapping: (code: string) =>
    request<QboTaxOverview>(`/qbo/tax-mappings/${code}/create`, { method: "POST" }),
  syncReceipt: (id: string) => request<QboSyncResult>(`/qbo/receipts/${id}/sync`, { method: "POST" }),
  syncBulk: (receiptIds?: string[]) =>
    request<QboBulkSyncResult>("/qbo/receipts/sync-bulk", {
      method: "POST",
      body: JSON.stringify({ receipt_ids: receiptIds ?? null }),
    }),
  activity: (limit = 50) => request<QboActivityRow[]>(`/qbo/activity${qs({ limit })}`),
};

// ─── Admin ─────────────────────────────────────────────────────────

export interface AdminOverview {
  users: number;
  mailboxes: number;
  mailboxes_failing: number;
  receipts: number;
  receipts_24h: number;
  messages_by_status: Partial<Record<MessageStatus, number>>;
  uploads_pending: number;
  open_reports: number;
  ai_24h: { calls: number; input_tokens: number; output_tokens: number; failures: number; avg_latency_ms: number };
  config: {
    ai_provider: string;
    ai_model: string;
    ai_reasoning_effort: string;
    ai_key_configured: boolean;
    qbo_mode: string;
    qbo_environment: string;
    gmail_configured: boolean;
    outlook_configured: boolean;
    public_url: string;
  };
}

export interface AdminUser extends User {
  mailbox_count: number;
  receipt_count: number;
}

export interface AdminMailbox extends EmailAccount {
  owner_email: string | null;
  message_count: number;
}

export interface AdminMessage extends EmailMessage {
  owner_email: string | null;
  account_email: string | null;
}

export interface AiCall {
  id: string;
  owner_user_id: string | null;
  purpose: string;
  email_message_id: string | null;
  upload_job_id: string | null;
  model: string;
  status: "ok" | "error" | "refusal";
  input_tokens: number;
  output_tokens: number;
  latency_ms: number;
  document_count: number;
  error: string | null;
  created_at: string;
  response?: Record<string, unknown> | null;
}

export interface AdminMessageDetail extends AdminMessage {
  body_text: string | null;
  body_html: string | null;
  attachments?: ReceiptAttachmentMeta[] | null;
  receipts: {
    id: string;
    vendor_name: string | null;
    total_cents: number | null;
    currency: string | null;
    receipt_type: string;
    discarded: boolean;
    discard_reason: string | null;
  }[];
  ai_calls: AiCall[];
}

export interface AdminReport extends ParseReport {
  owner_email: string | null;
  receipt_vendor: string | null;
  receipt_total_cents: number | null;
  receipt_currency: string | null;
  receipt_type: string | null;
  email_message_id: string | null;
}

export interface SenderBlock {
  id: string;
  match_type: "sender_email" | "sender_domain";
  pattern: string;
  notes: string | null;
  created_at: string;
}

export interface SystemSettings {
  allow_registration: boolean;
  ai_enabled: boolean;
  prefilter_enabled: boolean;
  prefilter_keywords: string[];
}

export const admin = {
  overview: () => request<AdminOverview>("/admin/overview"),
  users: (q?: string) => request<AdminUser[]>(`/admin/users${qs({ q })}`),
  createUser: (body: { email: string; password: string; full_name?: string; is_admin?: boolean }) =>
    request<User>("/admin/users", { method: "POST", body: JSON.stringify(body) }),
  updateUser: (id: string, body: { full_name?: string; is_admin?: boolean; is_active?: boolean; password?: string }) =>
    request<User>(`/admin/users/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteUser: (id: string) => request<void>(`/admin/users/${id}`, { method: "DELETE" }),
  mailboxes: (failing = false) => request<AdminMailbox[]>(`/admin/mailboxes${qs({ failing: failing || undefined })}`),
  updateMailbox: (id: string, body: { is_active?: boolean; poll_interval_seconds?: number }) =>
    request<EmailAccount>(`/admin/mailboxes/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  pollMailbox: (id: string) => request<{ status: string }>(`/admin/mailboxes/${id}/poll`, { method: "POST" }),
  messages: (filters: { status?: string; q?: string; owner_user_id?: string; limit?: number; offset?: number } = {}) =>
    request<AdminMessage[]>(`/admin/messages${qs(filters)}`),
  message: (id: string) => request<AdminMessageDetail>(`/admin/messages/${id}`),
  reparseMessage: (id: string) => request<{ status: string }>(`/admin/messages/${id}/reparse`, { method: "POST" }),
  receipt: (id: string) => request<Receipt>(`/admin/receipts/${id}`),
  reports: (filters: { status?: string; reason?: string } = {}) =>
    request<AdminReport[]>(`/admin/reports${qs({ status: filters.status ?? "", reason: filters.reason })}`),
  updateReport: (id: string, body: { status: ParseReportStatus; resolution_note?: string | null }) =>
    request<ParseReport>(`/admin/reports/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  reparseReport: (id: string) =>
    request<{ status: string; message: string }>(`/admin/reports/${id}/reparse`, { method: "POST" }),
  aiCalls: (filters: { status?: string; purpose?: string; limit?: number } = {}) =>
    request<AiCall[]>(`/admin/ai-calls${qs(filters)}`),
  blocklist: () => request<SenderBlock[]>("/admin/blocklist"),
  addBlock: (body: { match_type: SenderBlock["match_type"]; pattern: string; notes?: string | null }) =>
    request<SenderBlock>("/admin/blocklist", { method: "POST", body: JSON.stringify(body) }),
  removeBlock: (id: string) => request<void>(`/admin/blocklist/${id}`, { method: "DELETE" }),
  settings: () => request<SystemSettings>("/admin/settings"),
  updateSettings: (body: Partial<SystemSettings>) =>
    request<SystemSettings>("/admin/settings", { method: "PATCH", body: JSON.stringify(body) }),
};

// ─── Formatting helpers ────────────────────────────────────────────

export function formatMoney(cents: number | null | undefined, currency?: string | null): string {
  if (cents === null || cents === undefined) return "—";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD" }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency ?? ""}`.trim();
  }
}
